import React, { ReactNode, useEffect, useState } from 'react';

import { LucidTheme } from '../../types/types';
import Box from '../box/box';
import Icon from '../icon/icon';
import Link from '../link/link';

export interface PaginationRenderProps<T> {
  /** The current activePage number. */
  activePage: number;
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The totalPages that have been paginated. */
  totalPages: number;
  /** The visible items that get returned. */
  visibleItems: T[];
}

export type PaginationChildren<T> = ReactNode | ((props: PaginationRenderProps<T>) => ReactNode);

export interface PaginationProps<T> {
  /** The number of visible items before and after the ellipsis to always show. `@default = 1` */
  alwaysVisiblePageCount?: number;
  /** The children that are returned with the `Pagination` component. */
  children: PaginationChildren<T>;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The list of items to return with pagination. */
  items: T[];
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** Checks whether or not the items should always be visible. */
  shouldAlwaysBeVisible?: boolean;
  /** Returns all items not paginated. */
  shouldNotPaginate?: boolean;
  /** The number of items away from the start/finish before showing ellipsis. `@default = 5` */
  visiblePageCount?: number;
  /** The number of items to show after the ellipsis. `@default = 1` */
  visibleEndPageCount?: number;
}

export const childrenComponents = <T,>(
  children: ReactNode,
  { activePage, itemsPerPage, totalPages, visibleItems }: PaginationRenderProps<T>,
): PaginationChildren<T> => {
  if (typeof children === 'function') {
    return children({ activePage, itemsPerPage, totalPages, visibleItems });
  }

  return children;
};

const Pagination = <T,>({
  alwaysVisiblePageCount = 1,
  children,
  className,
  items,
  itemsPerPage = 5,
  shouldAlwaysBeVisible = false,
  shouldNotPaginate = false,
  visiblePageCount = 5,
  visibleEndPageCount = 1,
}: PaginationProps<T>) => {
  const [activePage, setActivePage] = useState(1);
  const totalPages = shouldNotPaginate ? 1 : Math.ceil(items.length / itemsPerPage);
  const willShowPrev = activePage > 1;
  const willShowNext = activePage < totalPages;
  const visibleItemStart = (activePage - 1) * itemsPerPage;
  const visibleItemEnd = activePage * itemsPerPage;
  const visibleItems = shouldNotPaginate ? items : items.slice(visibleItemStart, visibleItemEnd);
  const isSequential = (vals: number[]) => vals[1] === vals[0] + 1;
  const renderedChildren = childrenComponents(children, {
    activePage,
    itemsPerPage,
    totalPages,
    visibleItems,
  });
  let preChildren;
  let postChildren;

  if (shouldAlwaysBeVisible) {
    const preInvisibleItems = items.slice(0, visibleItemStart);
    if (preInvisibleItems.length > 0) {
      preChildren = childrenComponents(children, {
        activePage,
        itemsPerPage,
        totalPages,
        visibleItems: preInvisibleItems,
      });
    }

    const postInvisibleItems = items.slice(visibleItemEnd);
    if (postInvisibleItems.length > 0) {
      postChildren = childrenComponents(children, {
        activePage,
        itemsPerPage,
        totalPages,
        visibleItems: postInvisibleItems,
      });
    }
  }

  const currentVisiblePages = [...Array(totalPages).keys()]
    .map(i => i + 1)
    .filter(currentPage => {
      const isWithinStartTail = currentPage <= visibleEndPageCount;
      const isWithinEndTail = currentPage >= totalPages - visibleEndPageCount + 1;
      const isWithinNeighborDistance = Math.abs(currentPage - activePage) <= alwaysVisiblePageCount;
      const isWithinFirstEllipsisDistance =
        activePage <= visiblePageCount && currentPage <= visiblePageCount + alwaysVisiblePageCount;
      const isWithinLastEllipsisDistance =
        activePage > totalPages - visiblePageCount &&
        currentPage > totalPages - visiblePageCount - alwaysVisiblePageCount;

      return (
        isWithinStartTail ||
        isWithinEndTail ||
        isWithinNeighborDistance ||
        isWithinFirstEllipsisDistance ||
        isWithinLastEllipsisDistance
      );
    });

  if (currentVisiblePages.length > visiblePageCount) {
    const firstTwoItems = currentVisiblePages.slice(visibleEndPageCount - 1, visibleEndPageCount + 1);
    const lastTwoItems = currentVisiblePages.slice(
      -(visibleEndPageCount + 1),
      currentVisiblePages.length - visibleEndPageCount + 1,
    );

    if (!isSequential(firstTwoItems)) {
      const startEllipsisId = -1;
      currentVisiblePages.splice(visibleEndPageCount, 0, startEllipsisId);
    }

    if (!isSequential(lastTwoItems)) {
      const endEllipsisId = -2;
      currentVisiblePages.splice(currentVisiblePages.length - visibleEndPageCount, 0, endEllipsisId);
    }
  }

  useEffect(() => {
    setActivePage(1);
  }, [items]);

  return (
    <>
      {preChildren && <Box css={{ display: 'none' }}>{preChildren}</Box>}
      <Box
        css={{
          label: 'paginationChildren',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderedChildren}
      </Box>
      {postChildren && <Box css={{ display: 'none' }}>{postChildren}</Box>}
      <Box
        className={className}
        css={(theme: LucidTheme) => ({
          label: 'pagination',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingTop: theme.space.s32,
          paddingBottom: theme.space.s40,
        })}
      >
        {!shouldNotPaginate && totalPages > 1 && (
          <Box
            css={{
              label: 'paginationNavigation',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {willShowPrev && (
              <Link
                css={(theme: LucidTheme) => ({
                  color: theme.colors.coolGrey[50],
                  verticalAlign: 'middle',
                  outline: 'none',
                  '&:hover, &:focus': {
                    color: theme.colors.indigo[60],
                  },
                })}
                onClick={() => setActivePage(activePage - 1)}
                rel="prev"
                role="button"
              >
                <Icon
                  css={{
                    fontSize: '1rem',
                    transform: 'rotate(180deg)',
                    marginRight: '1rem',
                  }}
                  type="CarotRight"
                />
              </Link>
            )}
            {currentVisiblePages.map(currentPage =>
              currentPage > 0 ? (
                <Link
                  key={currentPage}
                  asButton
                  css={(theme: LucidTheme) => [
                    {
                      display: 'inline-block',
                      padding: 0,
                      width: '1.75rem',
                      height: '1.75rem',
                      fontSize: theme.fontSizes.fs16,
                      color: theme.colors.coolGrey[40],
                      borderRadius: '2px',
                      border: `1px solid ${theme.colors.coolGrey[20]}`,
                      boxShadow: 'none',
                      margin: theme.space.s8,
                      outline: 'none',
                      backgroundColor: theme.colors.white,
                      '&:hover, &:focus': {
                        color: theme.colors.indigo[60],
                        backgroundColor: theme.colors.white,
                        border: `1px solid ${theme.colors.indigo[60]}`,
                      },
                    },
                    activePage === currentPage && {
                      '&, &:hover, &:focus': {
                        backgroundColor: theme.colors.indigo[60],
                        color: theme.colors.white,
                        border: `1px solid ${theme.colors.indigo[60]}`,
                      },
                    },
                  ]}
                  isActive={activePage === currentPage}
                  onClick={() => setActivePage(currentPage)}
                  variant={activePage === currentPage ? 'primary' : 'secondary'}
                >
                  {currentPage}
                </Link>
              ) : (
                <span
                  key={currentPage}
                  css={(theme: LucidTheme) => ({
                    display: 'inline-block',
                    textAlign: 'center',
                    margin: theme.space.s8,
                    width: '1.75rem',
                  })}
                >
                  ...
                </span>
              ),
            )}
            {willShowNext ? (
              <Link
                css={(theme: LucidTheme) => ({
                  color: theme.colors.coolGrey[50],
                  verticalAlign: 'middle',
                  outline: 'none',
                  '&:hover, &:focus': {
                    color: theme.colors.indigo[60],
                  },
                })}
                onClick={() => setActivePage(activePage + 1)}
                rel="next"
                role="button"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    fontSize: theme.fontSizes.fs16,
                    marginLeft: theme.space.s16,
                  })}
                  type="CarotRight"
                />
              </Link>
            ) : (
              <span css={{ marginLeft: '2rem' }}></span>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export { Pagination };
export default Pagination;
